import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { cms } from "@/lib/cms";
import { Logo } from "@/components/landing/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, Upload, RefreshCw, Loader2, ExternalLink, LogOut, FileJson, Eye, EyeOff, LayoutGrid } from "lucide-react";
import { getFormFor } from "@/components/cms/SectionForms";

const JsonEditor = ({ value, onChange }) => {
  const [text, setText] = useState(JSON.stringify(value, null, 2));
  const [err, setErr] = useState(null);
  useEffect(() => { setText(JSON.stringify(value, null, 2)); }, [value]);
  return (
    <div className="space-y-2">
      <Textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          try { onChange(JSON.parse(e.target.value)); setErr(null); }
          catch (ex) { setErr(ex.message); }
        }}
        rows={20}
        className="font-mono text-xs leading-relaxed"
        data-testid="cms-json-editor"
      />
      {err && <p className="text-xs text-red-600">JSON error: {err}</p>}
    </div>
  );
};

const ImageUploader = ({ label, value, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const onPick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const r = await cms.upload(f);
      onUploaded(r.absolute || r.url);
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-center gap-3">
      {value && <img src={value} alt="preview" className="h-12 w-12 rounded-lg border border-slate-200 object-cover" />}
      <label className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 h-9 cursor-pointer">
        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {label || "Upload image"}
        <input type="file" accept="image/*" className="hidden" onChange={onPick} disabled={uploading} />
      </label>
    </div>
  );
};

export default function CMS() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialKey = searchParams.get("key") || null;
  const [sections, setSections] = useState([]);
  const [active, setActive] = useState(initialKey);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("form");

  const load = async () => {
    const list = await cms.list();
    setSections(list);
    const want = searchParams.get("key");
    if (want && list.some((x) => x.key === want)) {
      setActive(want);
    } else if (!active && list.length) {
      setActive(list[0].key);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const s = sections.find((x) => x.key === active);
    if (s) setDraft({ title: s.title, data: s.data, enabled: s.enabled, order: s.order });
    // Keep the URL in sync so the page is deep-linkable / shareable
    if (active && searchParams.get("key") !== active) {
      const next = new URLSearchParams(searchParams);
      next.set("key", active);
      setSearchParams(next, { replace: true });
    }
  }, [active, sections]);

  const save = async () => {
    setSaving(true);
    try {
      await cms.upsert(active, draft);
      toast.success("Saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const toggleEnabled = async (key) => {
    await cms.toggle(key);
    await load();
    toast.success("Section visibility updated");
  };

  const resetAll = async () => {
    if (!window.confirm("Reset ALL content to factory defaults? This cannot be undone.")) return;
    await cms.resetAll();
    await load();
    toast.success("Reset complete");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cms-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="inline-flex items-center text-xs font-bold px-2 py-1 rounded-full bg-blue-50 text-blue-700">CMS</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
              <ExternalLink className="h-3.5 w-3.5" /> View site
            </a>
            <Button variant="outline" size="sm" onClick={() => nav("/admin/leads")} className="rounded-full" data-testid="cms-go-leads">Leads</Button>
            <Button variant="outline" size="sm" onClick={() => { logout(); nav("/admin/login"); }} data-testid="cms-logout" className="rounded-full">
              <LogOut className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sections list */}
        <aside className="lg:col-span-3">
          <div className="rounded-2xl bg-white border border-slate-200 p-3" data-testid="cms-section-list">
            <div className="flex items-center justify-between px-2 py-2">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Sections</div>
              <button onClick={resetAll} title="Reset to factory defaults" className="text-[10px] font-semibold text-red-600 hover:text-red-700 flex items-center gap-1">
                <RefreshCw className="h-3 w-3" /> Reset
              </button>
            </div>
            <ul className="flex gap-1.5 overflow-x-auto no-scrollbar pb-1 lg:flex-col lg:overflow-visible lg:space-y-1">
              {sections.map((s) => (
                <li key={s.key} className="flex items-center gap-1 flex-shrink-0 lg:flex-shrink">
                  <button
                    onClick={() => setActive(s.key)}
                    data-testid={`cms-section-${s.key}`}
                    className={`flex-1 text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${active === s.key ? "bg-emerald-50 text-emerald-800" : "text-slate-700 hover:bg-slate-50"}`}
                  >
                    {s.title}
                    <span className="block text-[10px] text-slate-400 font-normal">{s.key}</span>
                  </button>
                  <button
                    onClick={() => toggleEnabled(s.key)}
                    title={s.enabled ? "Hide section" : "Show section"}
                    className={`h-8 w-8 flex items-center justify-center rounded-lg ${s.enabled ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-100"}`}
                    data-testid={`cms-toggle-${s.key}`}
                  >
                    {s.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Editor */}
        <section className="lg:col-span-9">
          {!draft ? (
            <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">Select a section…</div>
          ) : (
            <div className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-7" data-testid="cms-editor">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {draft.title}
                  </h1>
                  <p className="text-xs text-slate-500 mt-1 font-mono">key: {active}</p>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <Switch checked={draft.enabled} onCheckedChange={(v) => setDraft({ ...draft, enabled: v })} />
                    Section enabled
                  </label>
                  <Button onClick={save} disabled={saving} data-testid="cms-save-btn" className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save
                  </Button>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mt-6">
                <label className="text-sm">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Title</span>
                  <Input value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="mt-1.5" data-testid="cms-title-input" />
                </label>
                <label className="text-sm">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Order</span>
                  <Input type="number" value={draft.order ?? 0} onChange={(e) => setDraft({ ...draft, order: parseInt(e.target.value || 0) })} className="mt-1.5" />
                </label>
                <div className="text-sm">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Editor mode</span>
                  <div className="inline-flex rounded-full bg-slate-100 p-1">
                    <button onClick={() => setView("form")} data-testid="cms-view-form"
                      className={`px-3 h-8 rounded-full text-xs font-bold inline-flex items-center gap-1.5 ${view === "form" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>
                      <LayoutGrid className="h-3.5 w-3.5" /> Form
                    </button>
                    <button onClick={() => setView("json")} data-testid="cms-view-json"
                      className={`px-3 h-8 rounded-full text-xs font-bold inline-flex items-center gap-1.5 ${view === "json" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>
                      <FileJson className="h-3.5 w-3.5" /> JSON
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                {(() => {
                  const FormCmp = getFormFor(active);
                  if (view === "form" && FormCmp) {
                    return <FormCmp data={draft.data || {}} onChange={(data) => setDraft({ ...draft, data })} />;
                  }
                  return (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <FileJson className="h-4 w-4 text-slate-500" />
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Section data (JSON)</span>
                      </div>
                      <p className="text-[11px] text-slate-500 mb-2">
                        {FormCmp ? "Tip: switch to Form mode for a friendlier editor for this section." : "No form editor for this section yet — JSON-only."}
                      </p>
                      <JsonEditor value={draft.data || {}} onChange={(data) => setDraft({ ...draft, data })} />
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
